// Cosmetic items (locker, shop, orbs and the wizard look). Item ids are
// 8-digit strings, the same format as the account's `unlocks` list.
//
// Hats, robes, faces and wands are the creator's catalogue (round 5) with their
// rarities and shop prices. Every account owns the STARTER items (the classic
// wizard look and the starter wand); anything else must be
// in `unlocks`, bought in the shop or pulled from an orb.
import type { Rarity } from "./economy";

export const ITEM_CATEGORIES = [
  { id: "hat", name: "Magic Hats" },
  { id: "robe", name: "Robes" },
  { id: "reaction", name: "Reactions / Faces" },
  { id: "head", name: "Computer Heads" },
  { id: "wand", name: "Wands" },
] as const;

/**
 * Categories that were removed from the game (table themes, spellbook covers,
 * icon effects, music tracks, table extras, dice packs). Old accounts may still
 * list their ids in `unlocks` or `equipped`; those entries are ignored: unknown
 * item ids are skipped by ownedItemIds and unknown equipped slots are dropped
 * by EquippedSchema.
 */

export type ItemCategory = (typeof ITEM_CATEGORIES)[number]["id"];

/** Rarity order for sorting (best first). */
export const RARITY_ORDER: readonly Rarity[] = ["wow", "mythic", "legendary", "wicked", "rare", "common"];

export const RARITY_COLORS: Record<Rarity, string> = {
  common: "#c0c0c0",
  rare: "#3399ff",
  wicked: "#33cc33",
  legendary: "#ff9900",
  mythic: "#cc33ff",
  wow: "#ff3366",
};

export const RARITY_LABEL: Record<Rarity, string> = {
  common: "Common",
  rare: "Rare",
  wicked: "Wicked",
  legendary: "Legendary",
  mythic: "Mythical",
  wow: "WOW",
};

/** The 3D model to build for a hat (see the client's three/cosmetics.ts). */
export type HatShape =
  | "cone"
  | "catEars"
  | "viking"
  | "crown"
  | "drinkingCap"
  | "straw"
  | "topHat"
  | "featheredCap"
  | "volcano"
  | "dunce"
  | "halo"
  | "fishCap"
  | "miku";

/** The 3D model / pattern for a robe. */
export type RobeShape = "stars" | "plain" | "maid" | "fur" | "invisible" | "pigeon" | "hotCoat" | "angel" | "fish" | "digitalSuper" | "bigStars";

/**
 * The 3D model for a wand (see the client's three/wands.ts). The starter wand
 * has no look: it is the stylus, a plain black rod.
 */
export type WandShape = "stylus" | "classic" | "archmage" | "glowStick" | "catToy" | "fish";

/** How an item changes the wizard model (hat, robe, head, wand). */
export interface ItemLook {
  /** Main colour, CSS hex. */
  color: string;
  /** Second colour (trim, stars, screen glow), CSS hex. */
  accent: string;
  /** Hats: a HatShape. Robes: a RobeShape. Wands: a WandShape. Heads: crt | flat | round. */
  shape?: string;
}

export interface ItemDef {
  id: string;
  category: ItemCategory;
  name: string;
  rarity: Rarity;
  description: string;
  look?: ItemLook;
  /** Reactions / faces: the text shown on the TV face. */
  face?: string;
  /** Shop price in coins. Items without a price are not sold. */
  price?: number;
  /** Everyone owns it. */
  starter?: boolean;
}

export const ITEMS: readonly ItemDef[] = [
  // ---------- Hats ----------
  { id: "10000001", category: "hat", name: "Classic Wizard Hat", rarity: "common", starter: true, description: "The pointy hat every wizard starts with.", look: { color: "#3a2a8c", accent: "#ffd700", shape: "cone" } },
  { id: "10000101", category: "hat", name: "Cat Ears", rarity: "wicked", price: 1500, description: "Fluffy black ears with pink insides, bows and little bells.", look: { color: "#111111", accent: "#f2a0b8", shape: "catEars" } },
  { id: "10000102", category: "hat", name: "Viking", rarity: "rare", price: 750, description: "An iron helmet with two big horns.", look: { color: "#8a8f96", accent: "#efe6cf", shape: "viking" } },
  { id: "10000103", category: "hat", name: "Royalty", rarity: "legendary", price: 6000, description: "A gold crown set with jewels.", look: { color: "#e8b923", accent: "#d01c3c", shape: "crown" } },
  { id: "10000104", category: "hat", name: "Drinking Cap", rarity: "rare", price: 750, description: "Two cans and a long straw. Stay hydrated.", look: { color: "#d22b2b", accent: "#f2f2f2", shape: "drinkingCap" } },
  { id: "10000105", category: "hat", name: "Straw Hat", rarity: "wicked", price: 1500, description: "A woven straw hat with a red band, steaming hot.", look: { color: "#e9d39a", accent: "#c8102e", shape: "straw" } },
  { id: "10000106", category: "hat", name: "White Top Hat", rarity: "common", price: 300, description: "A tall white top hat with a hot pink band.", look: { color: "#f1f1ee", accent: "#ff1694", shape: "topHat" } },
  { id: "10000107", category: "hat", name: "Black Top Hat", rarity: "common", price: 300, description: "A tall black top hat.", look: { color: "#151515", accent: "#8a1c1c", shape: "topHat" } },
  { id: "10000108", category: "hat", name: "Purple Top Hat", rarity: "common", price: 300, description: "A tall purple top hat.", look: { color: "#5b2a8c", accent: "#ffd700", shape: "topHat" } },
  { id: "10000109", category: "hat", name: "Feathered Cap", rarity: "mythic", price: 30000, description: "A white pigeon has made its home on your head.", look: { color: "#2f6f4f", accent: "#ffffff", shape: "featheredCap" } },
  { id: "10000110", category: "hat", name: "Volcano", rarity: "legendary", price: 6000, description: "A smoking volcano with glowing lava.", look: { color: "#4a3a32", accent: "#ff5a1a", shape: "volcano" } },
  { id: "10000111", category: "hat", name: "Dunce Cap", rarity: "rare", price: 750, description: "A tall paper cone. It says DUNCE.", look: { color: "#f4f1e8", accent: "#333333", shape: "dunce" } },
  { id: "10000112", category: "hat", name: "Halo", rarity: "mythic", price: 30000, description: "A glowing gold ring that floats over your head.", look: { color: "#ffe680", accent: "#fff6c8", shape: "halo" } },
  { id: "10000113", category: "hat", name: "Fish Hat", rarity: "wow", price: 1_000_000, description: "A blue cap with a whole bass swimming through it.", look: { color: "#1fb0d6", accent: "#4a6b2a", shape: "fishCap" } },
  { id: "10000114", category: "hat", name: "Miku", rarity: "wow", price: 1_000_000, description: "Long teal twin tails, glowing pink headphones and a stream of music notes.", look: { color: "#39c5bb", accent: "#fc46aa", shape: "miku" } },

  // ---------- Robes ----------
  { id: "20000001", category: "robe", name: "Sleepy Purple Robe + Gold Stars", rarity: "common", price: 300, starter: true, description: "The default robe: sleepy purple with gold stars.", look: { color: "#3a2a8c", accent: "#ffd700", shape: "stars" } },
  { id: "20000101", category: "robe", name: "Maid Outfit", rarity: "wicked", price: 1500, description: "A black dress with a frilly white apron.", look: { color: "#161616", accent: "#ffffff", shape: "maid" } },
  { id: "20000102", category: "robe", name: "Fur Robe", rarity: "wicked", price: 1500, description: "A heavy brown robe with fluffy fur trim.", look: { color: "#6b4226", accent: "#efe3cf", shape: "fur" } },
  { id: "20000103", category: "robe", name: "Emperor's New Clothes", rarity: "legendary", price: 6000, description: "The finest robe ever made. Only the wise can see it.", look: { color: "#000000", accent: "#ffe680", shape: "invisible" } },
  { id: "20000104", category: "robe", name: "Tea Time Robe", rarity: "common", price: 300, description: "A white robe with big hot pink stars.", look: { color: "#f2f0f5", accent: "#ff1694", shape: "bigStars" } },
  { id: "20000105", category: "robe", name: "Black Robe", rarity: "common", price: 300, description: "A plain black robe.", look: { color: "#151515", accent: "#5a5a5a", shape: "plain" } },
  { id: "20000106", category: "robe", name: "Pigeon Costume", rarity: "mythic", price: 30000, description: "A white pigeon suit with wings, tail feathers and pink feet.", look: { color: "#f4f4f2", accent: "#e8728a", shape: "pigeon" } },
  { id: "20000107", category: "robe", name: "Hot Coat", rarity: "legendary", price: 6000, description: "A heavy coat of cracked grey stone, with glowing lava running down both sides to the hem.", look: { color: "#767a80", accent: "#ff4a10", shape: "hotCoat" } },
  { id: "20000108", category: "robe", name: "Angel Robe", rarity: "mythic", price: 30000, description: "A white and gold robe covered in eyes that slowly blink.", look: { color: "#f6f2e6", accent: "#d4af37", shape: "angel" } },
  { id: "20000109", category: "robe", name: "Fish Robe", rarity: "wow", price: 1_000_000, description: "A standard robe with a fish coming through the chest.", look: { color: "#3a2a8c", accent: "#ffd700", shape: "fish" } },
  { id: "20000110", category: "robe", name: "Idol", rarity: "wow", price: 1_000_000, description: "A grey sleeveless collared shirt with a long teal tie, a pleated skirt and detached sleeves.", look: { color: "#8e9496", accent: "#39c5bb", shape: "digitalSuper" } },

  // ---------- Reactions / faces (shown on your TV face) ----------
  { id: "90000101", category: "reaction", name: "Lenny", rarity: "wicked", price: 1000, face: "( ͡° ͜ʖ ͡°)", description: "( ͡° ͜ʖ ͡°)" },
  { id: "90000102", category: "reaction", name: "Shrug", rarity: "legendary", price: 4500, face: "¯\\_(ツ)_/¯", description: "¯\\_(ツ)_/¯" },
  { id: "90000103", category: "reaction", name: "Love Eyes", rarity: "common", price: 250, face: "♥‿♥", description: "♥‿♥" },
  { id: "90000104", category: "reaction", name: "Owl", rarity: "rare", price: 500, face: "◉v◉", description: "◉v◉" },
  { id: "90000105", category: "reaction", name: "Bear", rarity: "legendary", price: 4500, face: "ʕ·ᴥ·ʔ", description: "ʕ·ᴥ·ʔ" },
  { id: "90000106", category: "reaction", name: "OwO", rarity: "wicked", price: 500, face: "OωO", description: "OωO" },
  { id: "90000107", category: "reaction", name: "L", rarity: "common", price: 250, face: "L", description: "L" },
  { id: "90000108", category: "reaction", name: "W", rarity: "common", price: 250, face: "W", description: "W" },
  // PLACEHOLDER rarity and price: the creator's list gave none for this face.
  { id: "90000109", category: "reaction", name: "Sleepy Bear", rarity: "rare", price: 500, face: "￣(エ)￣", description: "￣(エ)￣" },
  { id: "90000110", category: "reaction", name: "Flower Smile", rarity: "wicked", price: 500, face: "✿◠‿◠✿", description: "✿◠‿◠✿" },
  { id: "90000111", category: "reaction", name: "Kitty", rarity: "mythic", price: 9000, face: "=･ω･=", description: "=･ω･=" },
  { id: "90000112", category: "reaction", name: "Smug", rarity: "wicked", price: 500, face: "✧¬‿¬", description: "✧¬‿¬" },

  // ---------- Computer heads ----------
  { id: "30000001", category: "head", name: "Beige CRT", rarity: "common", starter: true, description: "A trusty 14-inch monitor head.", look: { color: "#e8dfc8", accent: "#33ff66", shape: "crt" } },
  { id: "30000002", category: "head", name: "Bondi Blue", rarity: "common", starter: true, description: "See-through plastic. Very futuristic.", look: { color: "#1fa3c4", accent: "#e0ffff", shape: "round" } },
  { id: "30000003", category: "head", name: "Flat Panel", rarity: "common", starter: true, description: "Thin, sleek, and expensive.", look: { color: "#333333", accent: "#66ccff", shape: "flat" } },

  // ---------- Wands (prices are the creator's own, not the hat / robe ones) ----------
  { id: "40000001", category: "wand", name: "Stylus Wand", rarity: "common", starter: true, description: "A plain black rod. Tap to assemble." },
  { id: "40000101", category: "wand", name: "Archmage Wand", rarity: "rare", price: 300, description: "A tall dark staff whose sharp prongs cradle a glowing white orb.", look: { color: "#18151b", accent: "#f2f6ff", shape: "archmage" } },
  { id: "40000102", category: "wand", name: "Glow Stick", rarity: "wicked", price: 500, description: "A concert light stick with a frosted tube that glows bright blue.", look: { color: "#f4f4f2", accent: "#2f8bff", shape: "glowStick" } },
  { id: "40000103", category: "wand", name: "Common Wand", rarity: "common", price: 100, description: "A classic magician's wand: a black rod with brass tips.", look: { color: "#141414", accent: "#c9a24a", shape: "classic" } },
  { id: "40000104", category: "wand", name: "Cat Toy", rarity: "mythic", price: 850, description: "A stick and string with a fluffy pink bird swinging on the end.", look: { color: "#111111", accent: "#ff6fae", shape: "catToy" } },
  { id: "40000105", category: "wand", name: "Fish", rarity: "wow", price: 1750, description: "A blue fish on a red stick. It seems happy about it.", look: { color: "#2d4c8a", accent: "#b8383a", shape: "fish" } },
];

export const ITEMS_BY_ID: ReadonlyMap<string, ItemDef> = new Map(ITEMS.map((i) => [i.id, i]));

/** Everyone owns these. */
export const STARTER_ITEM_IDS: readonly string[] = ITEMS.filter((i) => i.starter).map((i) => i.id);

/** Items the shop sells (and orbs can give): priced and not a starter. */
export const SHOP_ITEMS: readonly ItemDef[] = ITEMS.filter((i) => i.price !== undefined && !i.starter);

/** Equipped item per category. */
export type Equipped = Partial<Record<ItemCategory, string>>;

/** What a new account wears. No face: the normal animated face. */
export const DEFAULT_EQUIPPED: Equipped = {
  hat: "10000001",
  robe: "20000001",
  head: "30000001",
  wand: "40000001",
};

/** The wizard look sent to other players (hat, robe, head and wand item ids). */
export interface WizardLook {
  hat: string;
  robe: string;
  head: string;
  /** Missing in looks saved before wands could be changed: the starter wand. */
  wand?: string;
}

/** The equipped item for a category, if it still exists in the catalogue and fits the slot. */
function slotItem(equipped: Equipped, category: "hat" | "robe" | "head" | "wand"): string {
  const id = equipped[category];
  const item = id ? ITEMS_BY_ID.get(id) : undefined;
  return item && item.category === category ? item.id : DEFAULT_EQUIPPED[category]!;
}

export function wizardLook(equipped: Equipped): WizardLook {
  return { hat: slotItem(equipped, "hat"), robe: slotItem(equipped, "robe"), head: slotItem(equipped, "head"), wand: slotItem(equipped, "wand") };
}

/** Items the account owns: starters plus unlocks that exist in the catalogue. */
export function ownedItemIds(unlocks: readonly string[]): string[] {
  const out = new Set(STARTER_ITEM_IDS);
  for (const id of unlocks) if (ITEMS_BY_ID.has(id)) out.add(id);
  return [...out];
}

/** Face items (reactions) among the owned items. */
export function ownedFaceIds(unlocks: readonly string[]): string[] {
  return ownedItemIds(unlocks).filter((id) => ITEMS_BY_ID.get(id)?.category === "reaction");
}
